import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useProfileContext } from '../main';

interface AgeVerificationContextType {
  isVerified: boolean;
  isUnder21: boolean;
  verify: (isOver21: boolean, birthDate?: Date) => void;
  reset: () => void;
}

const AgeVerificationContext = createContext<AgeVerificationContextType | undefined>(undefined);

export function AgeVerificationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { profile, updateProfile } = useProfileContext();
  
  const [isVerified, setIsVerified] = useState(() => {
    // If authenticated, use profile data, otherwise use session storage
    if (profile?.age_verified) {
      return true;
    }
    const stored = sessionStorage.getItem('age_verified');
    return stored === 'true';
  });

  const [isUnder21, setIsUnder21] = useState(false);
  
  // Initialize isUnder21 when profile loads
  useEffect(() => {
    if (profile?.age_verified && profile?.birth_date) {
      // Parse birth date and calculate age
      const birthDate = new Date(profile.birth_date);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      setIsUnder21(age < 21);
      setIsVerified(true);
    } else if (!user) {
      // For non-authenticated users, check session storage
      const stored = sessionStorage.getItem('under_21');
      setIsUnder21(stored === 'true');
    }
  }, [profile, user]);

  const verify = async (isOver21: boolean, birthDate?: Date) => {
    setIsVerified(true);
    setIsUnder21(!isOver21);
    
    // If user is logged in, update their profile
    if (user && birthDate) {
      try {
        await updateProfile({
          age_verified: true,
          birth_date: birthDate.toISOString()
        });
      } catch (error) {
      }
    } else {
      // Otherwise store in session
      sessionStorage.setItem('age_verified', 'true');
      sessionStorage.setItem('under_21', (!isOver21).toString());
    }
  };

  const reset = () => {
    // Only reset session storage, not the user profile
    setIsVerified(!!profile?.age_verified);
    setIsUnder21(false);
    sessionStorage.removeItem('age_verified');
    sessionStorage.removeItem('under_21');
  };

  return (
    <AgeVerificationContext.Provider value={{ isVerified, isUnder21, verify, reset }}>
      {children}
    </AgeVerificationContext.Provider>
  );
}

export function useAgeVerification() {
  const context = useContext(AgeVerificationContext);
  if (context === undefined) {
    throw new Error('useAgeVerification must be used within an AgeVerificationProvider');
  }
  return context;
}